import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Pencil, Plus, Trash2, X } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton, SkeletonText } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import {
  useAlertasGlobais,
  useAtualizarModelo,
  useCriarAlertaGlobal,
  useCriarModelo,
  useDesativarAlertaGlobal,
  useDesativarModelo,
  useModelosContrato,
} from '../hooks';
import type { ContractModel, ContractModelPayload } from '../api';

const ALERT_TYPE_LABEL: Record<string, string> = {
  vencimento: 'Vencimento',
  reajuste: 'Reajuste',
  garantia: 'Garantia',
};
const ALERT_TYPE_OPTIONS = Object.entries(ALERT_TYPE_LABEL).map(([value, label]) => ({ value, label }));

function GlobalAlertConfigPanel({ canEdit }: { canEdit: boolean }) {
  const { data: configs = [], isLoading } = useAlertasGlobais();
  const { mutate: criar, isPending: isCreating } = useCriarAlertaGlobal();
  const { mutate: desativar } = useDesativarAlertaGlobal();
  const [daysBefore, setDaysBefore] = useState('30');
  const [alertType, setAlertType] = useState('vencimento');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Bell className="w-4 h-4 text-gray-400" />
        Alertas Globais (padrão do sistema)
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Usados por qualquer contrato que não tenha configuração própria (RN-COM-010).
      </p>

      {isLoading ? (
        <SkeletonText lines={2} />
      ) : configs.length === 0 ? (
        <EmptyState size="compact" icon={<Bell className="h-8 w-8" />} title="Nenhum default global configurado ainda" />
      ) : (
        <ul className="divide-y divide-gray-100 text-sm mb-3">
          {configs.map((c) => (
            <li key={c.id} className="py-2 flex items-center justify-between">
              <span className="text-gray-700">
                {c.daysBefore} dias antes · {ALERT_TYPE_LABEL[c.alertType] ?? c.alertType}
              </span>
              {canEdit && (
                <button onClick={() => desativar(c.id)} aria-label="Remover" className="text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="flex items-end gap-2">
          <div className="w-32">
            <TextInput label="Dias de antecedência" type="number" min={1} value={daysBefore} onChange={(e) => setDaysBefore(e.target.value)} />
          </div>
          <div className="w-40">
            <AutocompleteSelect label="Tipo" options={ALERT_TYPE_OPTIONS} value={alertType} onChange={setAlertType} />
          </div>
          <Button
            size="sm"
            disabled={!daysBefore || Number(daysBefore) < 1}
            loading={isCreating}
            icon={<Plus className="w-3.5 h-3.5" />}
            onClick={() => criar({ daysBefore: Number(daysBefore), alertType })}
          >
            Adicionar
          </Button>
        </div>
      )}
    </div>
  );
}

function ModelForm({
  initial,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  initial?: ContractModel;
  onSubmit: (payload: ContractModelPayload) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [serviceType, setServiceType] = useState(initial?.serviceType ?? '');
  const [content, setContent] = useState(initial?.content ?? '');
  const [err, setErr] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) { setErr('Nome é obrigatório.'); return; }
    setErr('');
    onSubmit({
      name,
      description: description || undefined,
      serviceType: serviceType || undefined,
      content: content || undefined,
    });
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 mb-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{initial ? 'Editar modelo' : 'Novo modelo'}</h3>
        <button onClick={onCancel} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
      <TextInput label="Nome *" value={name} onChange={(e) => setName(e.target.value)} error={err} />
      <TextInput label="Tipo de serviço (ex: outsourcing, fabrica-software)" value={serviceType} onChange={(e) => setServiceType(e.target.value)} />
      <TextInput label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} />
      <Textarea label="Conteúdo do modelo" value={content} onChange={(e) => setContent(e.target.value)} rows={6} />
      <div className="flex gap-2">
        <Button loading={isSubmitting} onClick={handleSubmit}>Salvar</Button>
        <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

/** Administração de modelos de contrato por tipo de serviço (RN-COM-027). */
export default function ContratosModelosPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can(FEATURES.contratosModelos, 'create');
  const canEdit = can(FEATURES.contratosModelos, 'edit');
  const canDelete = can(FEATURES.contratosModelos, 'delete');

  const { data: models = [], isLoading } = useModelosContrato();
  const { mutate: criar, isPending: isCreating } = useCriarModelo();
  const { mutate: atualizar, isPending: isUpdating } = useAtualizarModelo();
  const { mutate: desativar } = useDesativarModelo();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ContractModel | null>(null);

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/contratos')} aria-label="Voltar" className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold text-gray-900">Modelos de Contrato</h1>
      </div>

      {canCreate && !showForm && (
        <Button size="sm" variant="secondary" icon={<Plus className="w-3.5 h-3.5" />} onClick={() => { setEditing(null); setShowForm(true); }}>
          Novo Modelo
        </Button>
      )}

      {showForm && (
        <ModelForm
          initial={editing ?? undefined}
          isSubmitting={isCreating || isUpdating}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={(payload) => {
            if (editing) {
              atualizar({ id: editing.id, payload }, { onSuccess: () => { setShowForm(false); setEditing(null); } });
            } else {
              criar(payload, { onSuccess: () => setShowForm(false) });
            }
          }}
        />
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="p-6"><Skeleton className="h-32 w-full" /></div>
        ) : models.length === 0 ? (
          <EmptyState title="Nenhum modelo cadastrado ainda" />
        ) : (
          <ul className="divide-y divide-gray-100">
            {models.map((m) => (
              <li key={m.id} className="p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                    {m.name}
                    {!m.isActive && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inativo</span>}
                  </p>
                  {m.serviceType && <p className="text-xs text-gray-500">Tipo de serviço: {m.serviceType}</p>}
                  {m.description && <p className="text-xs text-gray-500">{m.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canEdit && (
                    <button onClick={() => { setEditing(m); setShowForm(true); }} aria-label="Editar" className="text-gray-400 hover:text-blue-600">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && m.isActive && (
                    <button onClick={() => desativar(m.id)} aria-label="Desativar" className="text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <GlobalAlertConfigPanel canEdit={canEdit} />
    </div>
  );
}
